// ────────────────────────────────────────────────────────────────────────────
// HuerfanosPartesPage
// Administra partes diarios "huérfanos" (cerrados sin VB, cuyo supervisor
// está vacío o no matchea ningún usuario activo). Permite reasignarlos a
// un supervisor activo para que aparezcan en su bandeja "Mis VB".
// Solo admin/gerencia/rrhh_encargado.
// ────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback, useMemo } from 'react';
import { partesVbApi } from '../../lib/api';
import { useRegisterCmdKCommands } from '../../contexts/CmdKContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Loader2, UserX, UserCheck, AlertTriangle, RefreshCw, UserPlus, Search, KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';

export default function HuerfanosPartesPage() {
  const [loading, setLoading] = useState(true);
  const [reassigning, setReassigning] = useState(false);
  const [data, setData] = useState({ sin_supervisor: [], sin_user_activo: [], total_problematicos: 0 });
  const [supervisores, setSupervisores] = useState([]);
  const [tab, setTab] = useState('sin_user');
  const [q, setQ] = useState('');

  // Modal de reasignación
  const [modalParte, setModalParte] = useState(null);
  const [nuevoSupId, setNuevoSupId] = useState('');
  const [motivo, setMotivo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [huerfRes, supRes] = await Promise.all([
        partesVbApi.adminHuerfanos(),
        partesVbApi.adminSupervisoresActivos(),
      ]);
      setData(huerfRes.data || { sin_supervisor: [], sin_user_activo: [], total_problematicos: 0 });
      setSupervisores(supRes.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openReasignar = (parte) => {
    setModalParte(parte);
    setNuevoSupId('');
    setMotivo('');
  };

  const submitReasignar = async () => {
    if (!nuevoSupId) {
      toast.error('Seleccioná un supervisor');
      return;
    }
    setReassigning(true);
    try {
      const res = await partesVbApi.adminReasignarSupervisor(modalParte.id, nuevoSupId, motivo);
      toast.success(`Reasignado a ${res.data.supervisor_nuevo}`);
      setModalParte(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error reasignando');
    } finally {
      setReassigning(false);
    }
  };

  const filteredSinSup = useMemo(() => {
    const qn = q.trim().toLowerCase();
    if (!qn) return data.sin_supervisor;
    return data.sin_supervisor.filter((p) =>
      String(p.ot_principal ?? '').toLowerCase().includes(qn)
      || String(p.fecha ?? '').toLowerCase().includes(qn)
      || String(p.cerrado_por ?? '').toLowerCase().includes(qn),
    );
  }, [data.sin_supervisor, q]);

  const filteredSinUser = useMemo(() => {
    const qn = q.trim().toLowerCase();
    if (!qn) return data.sin_user_activo;
    return data.sin_user_activo.filter((p) =>
      String(p.supervisor_nombre ?? '').toLowerCase().includes(qn)
      || String(p.ot_principal ?? '').toLowerCase().includes(qn)
      || String(p.fecha ?? '').toLowerCase().includes(qn),
    );
  }, [data.sin_user_activo, q]);

  const supervisorSeleccionado = useMemo(
    () => supervisores.find((s) => s.id === nuevoSupId),
    [supervisores, nuevoSupId],
  );

  // ────────────────── Cmd+K: comandos contextuales ──────────────────
  useRegisterCmdKCommands(
    'partes-huerfanos',
    'Partes Huérfanos',
    [
      { id: 'huerf-refresh', label: 'Recargar listado', icon: 'RefreshCw',
        keywords: 'recargar refrescar actualizar', run: load },
      { id: 'huerf-tab-sin-user', label: 'Ir a tab "Sin user activo"',
        sublabel: `${data.sin_user_activo.length} partes`, icon: 'ArrowRight',
        run: () => setTab('sin_user'), enabled: tab !== 'sin_user' },
      { id: 'huerf-tab-sin-sup', label: 'Ir a tab "Sin supervisor"',
        sublabel: `${data.sin_supervisor.length} partes`, icon: 'ArrowRight',
        run: () => setTab('sin_sup'), enabled: tab !== 'sin_sup' },
      { id: 'huerf-clear-filter', label: 'Limpiar filtro de búsqueda',
        icon: 'X', run: () => setQ(''), enabled: !!q },
    ],
    [tab, q, data.sin_user_activo.length, data.sin_supervisor.length, load],
  );

  return (
    <div className="container mx-auto p-3 md:p-6 space-y-4" data-testid="page-huerfanos-partes">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            Partes Diarios Huérfanos
          </h1>
          <p className="text-sm text-muted-foreground">
            Partes cerrados pendientes de VB cuyo supervisor está vacío o no matchea un usuario activo.
            Reasignalos para destrabar el circuito de aprobación.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="btn-reload-huerfanos"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Recargar
        </Button>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card data-testid="card-total-huerfanos">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Total problemáticos</div>
            <div className="text-3xl font-bold text-amber-700 mt-1">{data.total_problematicos}</div>
          </CardContent>
        </Card>
        <Card data-testid="card-sin-supervisor">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <UserX className="w-3 h-3" /> Sin supervisor asignado
            </div>
            <div className="text-3xl font-bold text-red-700 mt-1">{data.sin_supervisor.length}</div>
          </CardContent>
        </Card>
        <Card data-testid="card-sin-user">
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <UserCheck className="w-3 h-3" /> Sin usuario activo que matchee
            </div>
            <div className="text-3xl font-bold text-orange-700 mt-1">{data.sin_user_activo.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtro */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input
          className="pl-8"
          placeholder="Buscar por OT, fecha, supervisor…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="input-filter-huerfanos"
        />
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 w-full max-w-md">
          <TabsTrigger value="sin_user" data-testid="tab-sin-user">
            <UserCheck className="w-4 h-4 mr-1" />
            Sin user activo
            {data.sin_user_activo.length > 0 && (
              <Badge className="ml-2 bg-orange-500">{data.sin_user_activo.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sin_sup" data-testid="tab-sin-sup">
            <UserX className="w-4 h-4 mr-1" />
            Sin supervisor
            {data.sin_supervisor.length > 0 && (
              <Badge className="ml-2 bg-red-500">{data.sin_supervisor.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sin_user" className="mt-3">
          <HuerfanosTable
            items={filteredSinUser}
            loading={loading}
            onReasignar={openReasignar}
            emptyMsg="No hay partes con supervisor no-matcheable 🎉"
            showSupervisor
          />
        </TabsContent>

        <TabsContent value="sin_sup" className="mt-3">
          <HuerfanosTable
            items={filteredSinSup}
            loading={loading}
            onReasignar={openReasignar}
            emptyMsg="No hay partes sin supervisor asignado 🎉"
            showSupervisor={false}
          />
        </TabsContent>
      </Tabs>

      {/* Modal: reasignar supervisor */}
      <Dialog open={!!modalParte} onOpenChange={(o) => !o && setModalParte(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-reasignar">
          {modalParte && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-emerald-600" />
                  Reasignar supervisor
                </DialogTitle>
                <DialogDescription>
                  Parte del <strong>{modalParte.fecha}</strong> · OT {modalParte.ot_principal || '—'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="bg-slate-50 dark:bg-slate-900 border rounded p-3 text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Supervisor actual:</span>
                    <span className={modalParte.supervisor_nombre ? 'font-medium' : 'italic text-muted-foreground'}>
                      {modalParte.supervisor_nombre || '(vacío)'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Personal:</span>
                    <span className="font-medium">{modalParte.personal_count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cerrado por:</span>
                    <span className="text-xs">{modalParte.cerrado_por || '—'}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Nuevo supervisor activo</label>
                  <Select value={nuevoSupId} onValueChange={setNuevoSupId}>
                    <SelectTrigger data-testid="select-supervisor">
                      <SelectValue placeholder="Seleccioná un supervisor…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {supervisores.length === 0 && (
                        <div className="p-2 text-sm text-muted-foreground">Sin usuarios activos</div>
                      )}
                      {supervisores.map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`option-sup-${s.id}`}>
                          <div className="flex items-center gap-2">
                            <span>{s.nombre}</span>
                            {s.tiene_pin_vb ? (
                              <Badge variant="outline" className="text-[10px] h-4 border-emerald-400 text-emerald-700">
                                <KeyRound className="w-2.5 h-2.5 mr-0.5" /> PIN
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[10px] h-4 border-amber-400 text-amber-700">
                                sin PIN
                              </Badge>
                            )}
                            {s.rol && (
                              <span className="text-[10px] text-muted-foreground">· {s.rol}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {supervisorSeleccionado && !supervisorSeleccionado.tiene_pin_vb && (
                    <p className="text-xs text-amber-700 mt-1 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      Este supervisor aún no configuró su PIN VB. Podrá verlo en 'Mis VB' pero no firmar hasta configurarlo.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Motivo (opcional)</label>
                  <Textarea
                    rows={2}
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    placeholder="Ej: Supervisor renunció, nombre mal escrito, etc."
                    maxLength={500}
                    data-testid="input-motivo"
                  />
                </div>

                <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded p-2">
                  El parte aparecerá en la bandeja 'Mis VB' del supervisor seleccionado.
                  Esta acción se registra en el audit log.
                </div>
              </div>
              <DialogFooter className="gap-2 flex-col sm:flex-row">
                <Button variant="outline" onClick={() => setModalParte(null)} disabled={reassigning}>
                  Cancelar
                </Button>
                <Button
                  onClick={submitReasignar}
                  disabled={reassigning || !nuevoSupId}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-confirmar-reasignar"
                >
                  {reassigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Reasignar
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tabla reutilizable para ambos tabs
// ────────────────────────────────────────────────────────────────────────────
function HuerfanosTable({ items, loading, onReasignar, emptyMsg, showSupervisor }) {
  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Cargando…
        </CardContent>
      </Card>
    );
  }
  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          {emptyMsg}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="table-huerfanos">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b">
              <tr className="text-xs uppercase text-muted-foreground">
                <th className="text-left px-3 py-2">Fecha</th>
                <th className="text-left px-3 py-2">OT</th>
                {showSupervisor && <th className="text-left px-3 py-2">Supervisor en parte</th>}
                <th className="text-left px-3 py-2">Personal</th>
                <th className="text-left px-3 py-2">Cerrado por</th>
                <th className="text-right px-3 py-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr
                  key={p.id}
                  className="border-b hover:bg-slate-50 dark:hover:bg-slate-900/50 transition"
                  data-testid={`row-huerfano-${p.id}`}
                >
                  <td className="px-3 py-2 font-mono text-xs">{p.fecha || '—'}</td>
                  <td className="px-3 py-2 font-medium">{p.ot_principal || '—'}</td>
                  {showSupervisor && (
                    <td className="px-3 py-2">
                      <Badge variant="outline" className="bg-orange-50 text-orange-800 border-orange-300">
                        {p.supervisor_nombre || '(vacío)'}
                      </Badge>
                    </td>
                  )}
                  <td className="px-3 py-2">{p.personal_count}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[200px]" title={p.cerrado_por || ''}>
                    {p.cerrado_por || '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onReasignar(p)}
                      data-testid={`btn-reasignar-${p.id}`}
                    >
                      <UserPlus className="w-3 h-3 mr-1" />
                      Reasignar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
